import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Space,
  Collapse,
  Row,
  Tooltip,
  Popconfirm,
  Checkbox,
  Col,
  Divider,
} from 'antd';
import { StyledField, StyledStandardFieldsWrapper } from '../styled';
import {
  CheckCircleTwoTone,
  MinusCircleOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { maOpsFields, standardAIData, standardMAData } from '../constant';
import { useMutation } from '@tanstack/react-query';
import { verifyWebhook } from '@src/api/notificationChannel';
import {
  getAlertCustomData,
  getAlertStandardData,
  getFriendlyErrorMessage,
  getMAOpsData,
  isAIAlert,
} from '../utils';
import { isAppModule } from '@src/utils/constants';
import { AlertType } from '../types';
const { Panel } = Collapse;

const AlertFieldsForm = ({
  formAlertType,
  validateFormFields,
  setVerifiedWebhookStatus,
  form,
  isFormChanged,
  webhookId,
}) => {
  const context = window.conviva.context;

  const [showStandardFields, setShowStandardFields] = useState(false);
  const [isVerifyError, setIsVerifyError] = useState(false);
  const [isVerifySuccess, setIsVerifySuccess] = useState(false);
  const [verifyErrorMessage, setVerifyErrorMessage] = useState('');
  const urlWatch = Form.useWatch('url', form);
  const authWatch = Form.useWatch('auth', form);
  const authUsernameWatch = Form.useWatch('username', form);
  const authPasswordWatch = Form.useWatch('password', form);
  const authTokenWatch = Form.useWatch('token', form);
  const alertAITypeWatch = Form.useWatch('alertAIType', form);
  const alertManualTypeWatch = Form.useWatch('alertManualType', form);
  const alertStandardFieldsWatch = Form.useWatch(`alert${formAlertType}Fields`, form);
  const alertCustomFieldsWatch = Form.useWatch(`custom${formAlertType}Fields`, form);
  const alertOptionalFieldsWatch = Form.useWatch('alertManualOpsFields', form);
  const alertOptionalCheckedFieldsWatch = Form.useWatch('checkedAlertManualOptFields', form);
  const isAI = isAIAlert(formAlertType);

  const ErrorDescription = ({ message }) => {
    return (
      <div>
        <p>{message}</p>
      </div>
    );
  };

  useEffect(() => {
    if (isFormChanged) {
      setIsVerifySuccess(false);
      setIsVerifyError(false);
      setVerifyErrorMessage('');
      setVerifiedWebhookStatus(false);
    }
  }, [
    urlWatch,
    authWatch,
    alertStandardFieldsWatch,
    alertCustomFieldsWatch,
    isAI ? alertAITypeWatch : alertManualTypeWatch,
    isAI ? alertOptionalCheckedFieldsWatch : null,
    isAI ? alertOptionalFieldsWatch : null,
  ]);

  const verifyWebhookMutation = useMutation(verifyWebhook, {
    onError: (e) => {
      // Failure in verifying webhook from IFS
      setIsVerifyError(true);
      setVerifiedWebhookStatus(false);
      const errorMessage = getFriendlyErrorMessage(e);
      setVerifyErrorMessage(errorMessage);
      setIsVerifySuccess(false);
    },
    onSuccess: (response) => {
      const { data: responseData } = response;
      // Success in verifying webhook from Notifier
      if (responseData.testStatus >= 200 && responseData.testStatus < 300) {
        setIsVerifyError(false);
        setIsVerifySuccess(true);
        setVerifiedWebhookStatus(true);
      } else {
        // Failure in verifying webhook from Notifier
        setIsVerifyError(true);
        setVerifiedWebhookStatus(false);
        const errorMessage = getFriendlyErrorMessage(responseData.data);
        setVerifyErrorMessage(errorMessage);
        setIsVerifySuccess(false);
      }
    },
  });

  const handleVerifyWebhook = async (alertType: AlertType) => {
    setIsVerifyError(false);
    setVerifyErrorMessage('');
    try {
      const alertFieldsKey = isAIAlert(alertType) ? 'alertAIFields' : 'alertManualFields';
      const customFieldsKey = isAIAlert(alertType) ? 'customAIFields' : 'customManualFields';
      const opsAlertFieldsKeys = isAIAlert(alertType)
        ? []
        : ['alertManualOpsFields', 'checkedAlertManualOptFields'];
      const { values, authValues } = await validateFormFields({
        alertFieldsKey,
        customFieldsKey,
        opsAlertFieldsKeys,
      });
      const alertFieldsRefactored = getAlertStandardData(values[alertFieldsKey], alertType);
      const customFieldsRefactored = getAlertCustomData(values[customFieldsKey] || []);
      const opsFieldsRefactored = getMAOpsData(
        values.alertManualOpsFields,
        values.checkedAlertManualOptFields,
      );

      const payload = {
        id: webhookId ?? null,
        webhookUrl: values.url,
        alertType: alertType,
        authentication: authValues,
        config: isAIAlert(alertType)
          ? {
              aiConfig: {
                alertFields: alertFieldsRefactored,
                customFields: customFieldsRefactored,
              },
            }
          : {
              maConfig: {
                alertFields: alertFieldsRefactored,
                maOpsFields: opsFieldsRefactored,
                customFields: customFieldsRefactored,
              },
            },
      };

      verifyWebhookMutation.mutate(payload);
    } catch (error) {
      setIsVerifyError(true);
      setIsVerifySuccess(false);
      console.error('Validation error:', error);
    }
  };

  const standardAlertFields = isAI ? standardAIData : standardMAData;

  const disableTestWebhook =
    isVerifySuccess ||
    !(authWatch
      ? isFormChanged &&
        !!urlWatch &&
        !!authWatch &&
        !!((authUsernameWatch && authPasswordWatch) || authTokenWatch)
      : isFormChanged && !!urlWatch) ||
    !(isAI ? alertAITypeWatch : alertManualTypeWatch);

  // matches strings with char length 3-255, can include spl chars, but should start with a alphanumeric char
  const customKeyPattern = /^[a-zA-Z0-9][\s\S]{2,254}$/;

  return (
    <Collapse defaultActiveKey={[`${formAlertType}key`]}>
      <Panel
        header={<div>{`Configure ${formAlertType} Alerts payload.`}</div>}
        key={`${formAlertType}key`}
      >
        <Form.Item
          name={`alert${formAlertType}Type`}
          valuePropName="checked"
          hidden={isAppModule}
          rules={[
            ({ getFieldValue }) => ({
              validator(_r, _v) {
                if (getFieldValue('alertManualType') || getFieldValue('alertAIType')) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(
                    context === 'app'
                      ? 'Please select alert type.'
                      : 'Please select atleast one alert type.',
                  ),
                );
              },
            }),
          ]}
        >
          <Checkbox>Include {formAlertType} Alerts in webhook.</Checkbox>
        </Form.Item>
        <Divider style={{ display: `${isAppModule ? 'none' : 'inherit'}` }} />
        <Form.Item>
          <p>A standard set of payload keys and values are included.</p>
          <Button onClick={() => setShowStandardFields(true)}>View Standard Fields</Button>
        </Form.Item>
        {showStandardFields ? (
          <StyledStandardFieldsWrapper>
            <Row className="standard-field-row">
              <StyledField>
                Key
                <Tooltip title="The keyname for the value field asset in Pulse.Greyed out keys are defined by Pulse and non-editable.">
                  <QuestionCircleOutlined />
                </Tooltip>
              </StyledField>
              <StyledField>
                Value (Example)
                <Tooltip title="The corresponding value for the key asset by the customer in Pulse or generated by Conviva as part of the alert condition.">
                  <QuestionCircleOutlined />
                </Tooltip>
              </StyledField>
              <StyledField>
                Customize Key Name
                <Tooltip title="Optional fields, that allow for customization of key name.">
                  <QuestionCircleOutlined />
                </Tooltip>
              </StyledField>
            </Row>
            {standardAlertFields.map((data, index) => {
              if (data.key === 'cumulative_impacted_unique_devices' && isAppModule) {
                return;
              }
              return (
                <Row key={index} className="standard-field-row">
                  <StyledField>
                    <Input placeholder={data.key} disabled />
                  </StyledField>
                  <StyledField>
                    <Input placeholder={`${data.value}`} disabled />
                  </StyledField>
                  <Form.Item
                    name={[`alert${formAlertType}Fields`, data.key]}
                    rules={[
                      {
                        pattern: customKeyPattern,
                        message:
                          'Key name must be between 3 and 256 characters, and begin with an alphanumeric character.',
                      },
                    ]}
                  >
                    <Input placeholder={data.exampleText} />
                  </Form.Item>
                </Row>
              );
            })}
            {formAlertType === 'Manual' ? (
              <>
                <Row className="standard-field-row optional">
                  <Col span={1}>
                    <StyledField> </StyledField>
                  </Col>
                  <StyledField>
                    Key
                    <Tooltip title="The keyname for the value field asset in Pulse.Greyed out keys are defined by Pulse and non-editable.">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </StyledField>
                  <StyledField>
                    Value (Example)
                    <Tooltip title="The corresponding value for the key asset by the customer in Pulse or generated by Conviva as part of the alert condition.">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </StyledField>
                  <StyledField>
                    Customize Key Name
                    <Tooltip title="Optional fields, that allow for customization of key name.">
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </StyledField>
                </Row>
                {maOpsFields.map((data, index) => {
                  return (
                    <Row key={index} className="standard-field-row optional">
                      <Col span={1}>
                        <Form.Item
                          name={['checkedAlertManualOptFields', data.key]}
                          valuePropName="checked"
                        >
                          <Checkbox />
                        </Form.Item>
                      </Col>

                      <StyledField>
                        <Input placeholder={data.key} disabled />
                      </StyledField>
                      <StyledField>
                        <Input placeholder={`${data.value}`} disabled />
                      </StyledField>
                      <Form.Item
                        name={['alertManualOpsFields', data.key]}
                        rules={[
                          {
                            pattern: customKeyPattern,
                            message:
                              'Key name must be between 3 and 256 characters, and begin with an alphanumeric character.',
                          },
                        ]}
                      >
                        <Input placeholder={data.exampleText} />
                      </Form.Item>
                    </Row>
                  );
                })}
              </>
            ) : null}
          </StyledStandardFieldsWrapper>
        ) : null}
        <Form.List name={`custom${formAlertType}Fields`}>
          {(fields, { add, remove }) => (
            <>
              <Form.Item>
                <p>You may customize this payload by adding custom fields.</p>
                <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                  Add Custom Field(s)
                </Button>
              </Form.Item>
              {fields.map(({ key, name, ...restField }) => (
                <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                  <Form.Item
                    {...restField}
                    name={[name, 'customKey']}
                    rules={[
                      { required: true, message: 'Missing Custom Key' },
                      {
                        pattern: customKeyPattern,
                        message:
                          'Key must be between 3 and 256 characters, and begin with an alphanumeric character.',
                      },
                    ]}
                  >
                    <Input placeholder="Custom Key" />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, 'customValue']}
                    rules={[{ required: true, message: 'Missing Custom Value' }]}
                  >
                    <Input placeholder="Custom Value" />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
              ))}
            </>
          )}
        </Form.List>
        <Divider />
        <Space
          align="center"
          direction="horizontal"
          style={{ width: '100%', justifyContent: 'end' }}
        >
          <Button
            type="primary"
            disabled={disableTestWebhook}
            ghost
            onClick={() => handleVerifyWebhook(formAlertType)}
            loading={verifyWebhookMutation.isLoading}
          >
            Test Webhooks
          </Button>
          {isVerifyError && (
            <Popconfirm
              placement="topLeft"
              title="Webhook Error"
              description={<ErrorDescription message={verifyErrorMessage} />}
              okText="okay"
              showCancel={false}
            >
              <Button type="link" icon={<WarningOutlined />} danger />
            </Popconfirm>
          )}
          {isVerifySuccess && (
            <Button type="link" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} />
          )}
        </Space>
        {!disableTestWebhook ? (
          <div style={{ display: 'flex', justifyContent: 'end', color: 'red' }}>
            Please test the webhook in order to save.
          </div>
        ) : null}
      </Panel>
    </Collapse>
  );
};

export default AlertFieldsForm;
